import { Box, Button, MenuItem, Select, TextField } from '@mui/material';
import React, { useState } from 'react';
import { useAuthStore } from '../../../store/storeAuth';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';
import type { FieldDefinition } from '../types';

interface CredentialsFormProps {
  provider: string;
  providerName: string;
  fields: FieldDefinition[];
  onSuccess?: (integrationId: string, name: string) => void;
  disabled?: boolean;
}

const CredentialsForm: React.FC<CredentialsFormProps> = ({
  provider,
  providerName,
  fields,
  onSuccess,
  disabled,
}) => {
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);
  const { addConnection, fetchConnections } = useStoreThirdPartyIntegration();

  const [connectionName, setConnectionName] = useState('');
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    fields.forEach(f => {
      initial[f.name] = (f.defaultValue as string) ?? '';
    });
    return initial;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (name: string, value: unknown) => {
    setValues(prev => ({ ...prev, [name]: String(value ?? '') }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await ThirdPartyIntegrationService.connect(provider, {
        mode: 'credentials',
        connectionName: connectionName.trim() || undefined,
        ...values,
      });
      const result = response?.data as any;
      const integrationId = result?.integrationId ?? '';
      const name = result?.name ?? (connectionName || providerName);

      if (integrationId) {
        addConnection(provider, {
          id: integrationId,
          name,
          provider,
          ownerUserId: user?.id ?? '',
          ownerUserName: (user as any)?.name ?? (user as any)?.email ?? '',
          isOwn: true,
        });
      }

      await fetchConnections(provider);
      onSuccess?.(integrationId, name);
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputSx = {
    width: '100%',
    maxWidth: '360px',
    '& .MuiOutlinedInput-root': {
      bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
      '& fieldset': { borderColor: user?.theme === 'dark' ? '#475467' : '#D0D5DD' },
    },
  };

  const selectSx = {
    width: '100%',
    maxWidth: '360px',
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    borderRadius: '8px',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
    bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
  };

  const labelSx = {
    display: 'block',
    fontSize: '14px',
    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
    fontWeight: 400,
    mb: 0.5,
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Box component="label" sx={labelSx}>
          Connection Name
        </Box>
        <TextField
          className="nodrag"
          size="small"
          fullWidth
          placeholder={`e.g. My ${providerName}`}
          value={connectionName}
          onChange={e => setConnectionName(e.target.value)}
          disabled={disabled}
          sx={inputSx}
        />
      </Box>

      {fields.map(field => {
        const fieldLabel = (
          <Box component="label" sx={labelSx}>
            {field.label}
            {field.validation?.required && ' *'}
          </Box>
        );

        if (field.type === 'select' && Array.isArray(field.options)) {
          const options = field.options;
          const current = values[field.name] ?? '';
          const selectedLabel = options.find(o => o.value === current)?.label;
          return (
            <Box key={field.name}>
              {fieldLabel}
              <Select
                className="nodrag"
                size="small"
                fullWidth
                value={current}
                onChange={e => handleChange(field.name, e.target.value)}
                disabled={disabled}
                required={field.validation?.required}
                sx={selectSx}
                displayEmpty
                renderValue={() =>
                  selectedLabel ?? field.placeholder ?? `Select ${field.label}`
                }
              >
                {options.map(opt => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    sx={{ color: user?.theme === 'dark' ? '#D0D5DD' : 'black' }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          );
        }

        return (
          <Box key={field.name}>
            {fieldLabel}
            <TextField
              className="nodrag"
              size="small"
              fullWidth
              type={field.name.toLowerCase().includes('password') ? 'password' : 'text'}
              placeholder={field.placeholder}
              value={values[field.name] ?? ''}
              onChange={e => handleChange(field.name, e.target.value)}
              disabled={disabled}
              required={field.validation?.required}
              inputProps={{ maxLength: field.validation?.maxLength }}
              sx={inputSx}
            />
          </Box>
        );
      })}
      <Button
        type="submit"
        variant="contained"
        size="small"
        disabled={disabled || isSubmitting}
        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
      >
        {isSubmitting ? 'Connecting...' : `Connect to ${providerName}`}
      </Button>
    </Box>
  );
};

export default CredentialsForm;
